import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { Field, FormActions, FormGroup, FormPanel, Select, TextArea } from '@/components/ui';
import { useMarkSaved } from '@/components/unsavedForm';
import { formatDate, formatDateTime, formatMoney, formatTime } from '@/format';
import { useAuth } from '@/stores/auth';
import {
  DEFAULT_COMPANY,
  useCompany,
  type CompanyPatch,
  type CompanySettings,
  type DateFormat,
  type NumberGrouping,
  type TimeFormat,
} from '@/stores/company';
import { toast } from '@/stores/toast';
import { LoginPicturePanel } from './LoginPicturePanel';

export const COMPANY_PERMISSION = 'iam.company.update';

const LOGO_MAX_BYTES = 200 * 1024;
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const IDLE_CHOICES = [0, 5, 10, 15, 30, 60, 120, 240];

/** Every IANA time zone the browser knows, or a short list where it cannot say. */
function timeZones(current: string): string[] {
  const all =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : ['Asia/Karachi', 'Asia/Dubai', 'Asia/Riyadh', 'Europe/London', 'UTC'];
  return all.includes(current) ? all : [current, ...all];
}

/**
 * Settings › Company: the name and logo on printouts and the sign-in screen,
 * and the formats every screen uses for money, numbers, dates and times.
 * Shared by everyone, so it saves on an explicit Save (not as you type like
 * personal settings) and only people allowed to change it can.
 */
export function CompanyPage() {
  const can = useAuth((s) => s.can);
  const company = useCompany((s) => s.company);
  const editable = can(COMPANY_PERMISSION);

  return (
    <section className="settingsapp__section" aria-labelledby="settings-company">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-company">Company</h2>
          <p className="muted settings__intro">
            {editable
              ? 'These details and formats are the same for everyone in the company.'
              : 'These details and formats are the same for everyone. Only an administrator can change them.'}
          </p>
        </div>
      </header>
      {/* Keyed by the saved version, so a save in another window refreshes the form. */}
      <FormPanel title="Company details">
        <CompanyForm key={company.updatedAt ?? 'default'} saved={company} editable={editable} />
      </FormPanel>
      <LoginPicturePanel editable={editable} />
    </section>
  );
}

function CompanyForm({ saved, editable }: { saved: CompanySettings; editable: boolean }) {
  const save = useCompany((s) => s.save);
  const markSaved = useMarkSaved();
  const [draft, setDraft] = useState<CompanySettings>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changes = useMemo(() => diff(saved, draft), [saved, draft]);
  const changed = Object.keys(changes).length > 0;
  const zones = useMemo(() => timeZones(draft.timezone), [draft.timezone]);

  function set<K extends keyof CompanySettings>(key: K, value: CompanySettings[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  /** Text boxes: empty clears an optional field. */
  const text = (key: 'nameUr' | 'address' | 'phone' | 'email' | 'taxNumber' | 'printFooter') => (v: string) =>
    set(key, v.trim() === '' ? null : v);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!changed) return;
    setError(null);
    if (!draft.nameEn.trim()) return setError('Enter the company name.');
    setBusy(true);
    try {
      await save(changes);
      markSaved();
      toast.success('Company settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function onLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setError('The logo must be a PNG or JPEG picture.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError('The logo is too big. Use a picture under 200 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError(null);
      set('logoDataUrl', String(reader.result));
    };
    reader.readAsDataURL(file);
  }

  const disabled = !editable || busy;
  const sample = new Date();

  return (
    <form onSubmit={submit} className="stack companyform">
      <FormGroup title="Name and contact" hint="Printed at the top of every document." columns={2}>
        <Field label="Company name" value={draft.nameEn} onChange={(v) => set('nameEn', v)} required disabled={disabled} />
        <Field label="Company name in Urdu" urdu value={draft.nameUr ?? ''} onChange={text('nameUr')} disabled={disabled} />
        <Field label="Phone" value={draft.phone ?? ''} onChange={text('phone')} disabled={disabled} />
        <Field label="Email" type="email" value={draft.email ?? ''} onChange={text('email')} disabled={disabled} />
        <Field
          label="Tax number (NTN or STRN)"
          value={draft.taxNumber ?? ''}
          onChange={text('taxNumber')}
          disabled={disabled}
        />
        <TextArea label="Address" value={draft.address ?? ''} onChange={text('address')} disabled={disabled} rows={2} />
      </FormGroup>

      <FormGroup
        title="Logo"
        hint="PNG or JPEG, under 200 KB. Shown on printouts and the sign-in screen. The sign-in picture is set below."
      >
        <div className="companyform__logo">
          {draft.logoDataUrl ? (
            <img src={draft.logoDataUrl} alt="Company logo" className="companyform__logoimg" />
          ) : (
            <span className="muted">No logo</span>
          )}
          {editable && (
            <>
              <label className="btn btn--sm">
                {draft.logoDataUrl ? 'Change logo' : 'Add logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={onLogo}
                  disabled={disabled}
                  className="sr-only"
                />
              </label>
              {draft.logoDataUrl && (
                <button type="button" className="btn btn--sm" onClick={() => set('logoDataUrl', null)} disabled={disabled}>
                  Remove logo
                </button>
              )}
            </>
          )}
        </div>
      </FormGroup>

      <FormGroup title="Money and numbers" columns={2}>
        <Field
          label="Currency code"
          value={draft.currencyCode}
          onChange={(v) => set('currencyCode', v.toUpperCase().slice(0, 3))}
          mono
          disabled={disabled}
          hint="Three letters, such as PKR."
        />
        <Field
          label="Currency symbol"
          value={draft.currencySymbol}
          onChange={(v) => set('currencySymbol', v.slice(0, 6))}
          disabled={disabled}
          hint="Shown before amounts, such as Rs."
        />
        <Select
          label="Decimal places for money"
          value={String(draft.currencyDecimals)}
          onChange={(v) => set('currencyDecimals', Number(v))}
          options={[0, 1, 2, 3].map((n) => ({ value: String(n), label: n === 0 ? 'None (Rs 1,250)' : String(n) }))}
          disabled={disabled}
          hint="For whole amounts. Paisa always show when an amount has them, such as Rs 20.40."
        />
        <Select
          label="Digit grouping"
          value={draft.numberGrouping}
          onChange={(v) => set('numberGrouping', v as NumberGrouping)}
          options={[
            { value: 'international', label: 'International (1,234,567)' },
            { value: 'lakh', label: 'Lakh and crore (12,34,567)' },
          ]}
          disabled={disabled}
        />
      </FormGroup>

      <FormGroup title="Dates and time" columns={2}>
        <Select
          label="Date format"
          value={draft.dateFormat}
          onChange={(v) => set('dateFormat', v as DateFormat)}
          options={[
            { value: 'DD/MM/YYYY', label: 'Day/Month/Year (29/09/2026)' },
            { value: 'DD-MMM-YYYY', label: 'Day-Month name-Year (29-Sep-2026)' },
            { value: 'YYYY-MM-DD', label: 'Year-Month-Day (2026-09-29)' },
            { value: 'MM/DD/YYYY', label: 'Month/Day/Year (09/29/2026)' },
          ]}
          disabled={disabled}
        />
        <Select
          label="Clock"
          value={draft.timeFormat}
          onChange={(v) => set('timeFormat', v as TimeFormat)}
          options={[
            { value: '12h', label: '12 hour (3:45 pm)' },
            { value: '24h', label: '24 hour (15:45)' },
          ]}
          disabled={disabled}
        />
        <Select
          label="Time zone"
          value={draft.timezone}
          onChange={(v) => set('timezone', v)}
          options={zones.map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))}
          disabled={disabled}
          hint="Every time on every screen is shown in this zone."
        />
        <Select
          label="Week starts on"
          value={String(draft.weekStartsOn)}
          onChange={(v) => set('weekStartsOn', Number(v))}
          options={DAYS.map((d, i) => ({ value: String(i), label: d }))}
          disabled={disabled}
        />
        <Select
          label="Financial year starts in"
          value={String(draft.fiscalYearStartMonth)}
          onChange={(v) => set('fiscalYearStartMonth', Number(v))}
          options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
          disabled={disabled}
          hint="Pakistan's financial year starts in July."
        />
      </FormGroup>

      <div className="companyform__sample" aria-live="polite">
        <span className="field__label">Example with these settings</span>
        <SampleLine draft={draft} when={sample} />
      </div>

      <FormGroup title="Security and printing" columns={2}>
        <Select
          label="Lock the screen after"
          value={String(draft.idleLockMinutes)}
          onChange={(v) => set('idleLockMinutes', Number(v))}
          options={IDLE_CHOICES.map((m) => ({
            value: String(m),
            label: m === 0 ? 'Never' : m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? 's' : ''}`,
          }))}
          disabled={disabled}
          hint="When nobody uses the app for this long, it asks for the password again."
        />
        <TextArea
          label="Footer on printouts"
          value={draft.printFooter ?? ''}
          onChange={text('printFooter')}
          disabled={disabled}
          rows={2}
          hint="For example: This is a computer-made document and needs no signature."
        />
      </FormGroup>

      {error && (
        <p className="errornote" role="alert">
          <span aria-hidden="true">⚠ </span>
          {error}
        </p>
      )}

      {saved.updatedAt && (
        <p className="muted settings__hint">
          Last changed {formatDateTime(saved.updatedAt)}
          {saved.updatedByName ? ` by ${saved.updatedByName}` : ''}.
        </p>
      )}

      {editable && (
        <FormActions
          cancelLabel="Undo changes"
          onCancel={() => {
            setDraft(saved);
            setError(null);
            markSaved();
          }}
        >
          <button type="submit" className="btn btn--primary" disabled={busy || !changed}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </FormActions>
      )}
    </form>
  );
}

/** The draft formats, before they are saved. */
function SampleLine({ draft, when }: { draft: CompanySettings; when: Date }) {
  const line = [
    // Whole amounts show the chosen decimals; an amount with paisa always shows them.
    formatMoney('1234567', draft),
    formatMoney('1250.5', draft),
    formatDate(when, draft),
    formatTime(when, draft),
  ].join(' · ');
  return <p className="companyform__sampleline">{line}</p>;
}

/** Only the fields that differ, as the PATCH body. */
function diff(saved: CompanySettings, draft: CompanySettings): CompanyPatch {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(DEFAULT_COMPANY) as Array<keyof CompanyPatch>) {
    if (draft[key] !== saved[key]) out[key] = draft[key];
  }
  return out as CompanyPatch;
}
